import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../core/components/Modal.tsx';
import FormField from '../../../core/components/FormField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { PROMPT_VARIABLES } from '../promptContext.ts';

const ExternalLinkIcon = withIcon(faExternalLink);

// The caller-context dynamic variables a tester can pre-fill (system__* variables are set automatically by ElevenLabs).
const TEST_VARIABLES = PROMPT_VARIABLES.filter((variable) => !variable.name.startsWith('system__'));

const VoiceAgentTestModal = ({ agentLink, onClose }: { agentLink: string; onClose: () => void }) => {
    const { t } = useTranslation('voice_agents');
    const [values, setValues] = useState<Record<string, string>>({});
    const [generatedLink, setGeneratedLink] = useState('');

    const handleGenerateLink = () => {
        const url = new URL(agentLink);
        // ElevenLabs' talk-to page reads dynamic variables from `var_<name>` query parameters.
        for (const variable of TEST_VARIABLES) {
            const value = values[variable.name];
            if (value) url.searchParams.set(`var_${variable.name}`, value);
        }
        setGeneratedLink(url.toString());
    };

    return (
        <Modal
            title={t('testModal.title')}
            onClose={onClose}
            buttons={
                <>
                    <Button onClick={onClose}>{t('testModal.cancel')}</Button>
                    <Button variant="primary" onClick={handleGenerateLink}>
                        {t('testModal.generateLink')}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                <p className="text-sm text-content-medium">{t('testModal.intro')}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {TEST_VARIABLES.map((variable) => (
                        <FormField key={variable.name} label={variable.label || variable.name}>
                            <StringField
                                value={values[variable.name] || ''}
                                onChange={(e) => setValues((prev) => ({ ...prev, [variable.name]: e.target.value }))}
                                placeholder={`{{${variable.name}}}`}
                            />
                        </FormField>
                    ))}
                </div>
                {generatedLink && (
                    <a
                        href={generatedLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-lg border border-solid border-line-weak bg-surface-weak p-3 text-content-brand-default transition-colors hover:bg-surface-medium"
                    >
                        <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium">{t('testModal.openLink')}</span>
                    </a>
                )}
            </div>
        </Modal>
    );
};

export default VoiceAgentTestModal;
